import React, { useState } from 'react';
import { format, differenceInDays } from 'date-fns';
import { motion } from 'framer-motion';
import { useCRM } from '../../context/CRMContext';
import { useAuth } from '../../context/AuthContext';
import Card from '../UI/Card';
import Button from '../UI/Button';
import Modal from '../UI/Modal';
import LeadForm from '../Forms/LeadForm';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEdit, FiTrash2, FiMail, FiPhone, FiCalendar, FiDollarSign, FiUser, FiClock, FiAlertTriangle, FiTarget, FiBell, FiMessageSquare } = FiIcons;

const LeadCard = ({ lead, onEdit, onDelete, onConvert }) => {
  const { users, leadSources } = useCRM();
  const { user: currentUser, isAdmin, isManager } = useAuth();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const assignedUser = users?.find(u => u.id === lead.assignedTo);
  const leadSource = leadSources?.find(s => s.id === lead.leadSourceId);
  
  const canEdit = isAdmin || isManager || currentUser.id === lead.assignedTo;
  const canDelete = isAdmin || isManager;

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'contacted': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'qualified': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'proposal_sent': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'negotiation': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'converted': return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      case 'lost': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'follow_up': return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getReminderStatus = () => {
    if (!lead.reminderDateTime) return null;
    
    const reminderDate = new Date(lead.reminderDateTime);
    const now = new Date();
    const daysDiff = differenceInDays(reminderDate, now);
    
    if (daysDiff < 0) {
      return { type: 'overdue', message: `Reminder overdue by ${Math.abs(daysDiff)} days` };
    } else if (daysDiff === 0) {
      return { type: 'today', message: 'Reminder due today' };
    } else if (daysDiff <= 2) {
      return { type: 'soon', message: `Reminder in ${daysDiff} days` };
    }
    
    return null;
  };

  const reminderStatus = getReminderStatus();

  const handleEdit = (data) => {
    onEdit(lead.id, data);
    setIsEditModalOpen(false);
  };

  const handleConvertToClient = () => {
    if (window.confirm('Convert this lead to a client?')) {
      onConvert(lead);
    }
  };

  const quickStatusButtons = [
    { status: 'contacted', label: 'Mark Contacted', variant: 'primary' },
    { status: 'qualified', label: 'Mark Qualified', variant: 'primary' },
    { status: 'proposal_sent', label: 'Proposal Sent', variant: 'secondary' },
    { status: 'converted', label: 'Mark Converted', variant: 'primary' }
  ];

  return (
    <>
      <Card className={`p-6 hover:shadow-lg transition-all ${reminderStatus?.type === 'overdue' ? 'border-l-4 border-red-500' : reminderStatus?.type === 'today' ? 'border-l-4 border-orange-500' : reminderStatus?.type === 'soon' ? 'border-l-4 border-yellow-500' : ''}`}>
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {lead.leadName}
              {lead.company && (
                <span className="text-sm text-gray-600 ml-2">- {lead.company}</span>
              )}
            </h3>
            <div className="flex flex-wrap gap-2">
              <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(lead.status)}`}>
                {lead.status.replace('_', ' ')}
              </span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(lead.priority)}`}>
                {lead.priority} priority
              </span>
            </div>
          </div>
          {canEdit && (
            <div className="flex space-x-1">
              <button
                onClick={() => setIsEditModalOpen(true)}
                className="text-gray-400 hover:text-primary-600 transition-colors"
              >
                <SafeIcon icon={FiEdit} className="h-4 w-4" />
              </button>
              {canDelete && (
                <button
                  onClick={() => onDelete(lead.id)}
                  className="text-gray-400 hover:text-red-600 transition-colors"
                >
                  <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Contact Info */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center text-sm text-gray-600">
            <SafeIcon icon={FiMail} className="h-4 w-4 mr-2" />
            <a href={`mailto:${lead.leadEmail}`} className="hover:text-primary-600">
              {lead.leadEmail}
            </a>
          </div>
          {lead.leadPhone && (
            <div className="flex items-center text-sm text-gray-600">
              <SafeIcon icon={FiPhone} className="h-4 w-4 mr-2" />
              <a href={`tel:${lead.leadPhone}`} className="hover:text-primary-600">
                {lead.leadPhone}
              </a>
            </div>
          )}
          <div className="flex items-center text-sm text-gray-600">
            <SafeIcon icon={FiUser} className="h-4 w-4 mr-2" />
            <span>Assigned to: {assignedUser?.fullName || assignedUser?.full_name || 'Unassigned'}</span>
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <SafeIcon icon={FiTarget} className="h-4 w-4 mr-2" />
            <span>Source: {leadSource?.name || 'Unknown'}</span>
          </div>
        </div>

        {/* Dates */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center text-sm text-gray-600">
            <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2" />
            <span>Lead Date: {format(new Date(lead.dateOfLead), 'MMM dd, yyyy')}</span>
          </div>
          {lead.contactedDate && (
            <div className="flex items-center text-sm text-gray-600">
              <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2" />
              <span>Contacted: {format(new Date(lead.contactedDate), 'MMM dd, yyyy')}</span>
            </div>
          )}
          {lead.reminderDateTime && (
            <div className="flex items-center text-sm text-gray-600">
              <SafeIcon icon={FiBell} className="h-4 w-4 mr-2" />
              <span>Reminder: {format(new Date(lead.reminderDateTime), 'MMM dd, yyyy HH:mm')}</span>
            </div>
          )}
        </div>

        {/* Estimated Value */}
        {lead.estimatedValue && (
          <div className="mb-4">
            <div className="flex items-center text-sm text-gray-600">
              <SafeIcon icon={FiDollarSign} className="h-4 w-4 mr-2" />
              <span>Est. Value: ${parseFloat(lead.estimatedValue).toLocaleString()}</span>
            </div>
          </div>
        )}

        {/* Required Services */}
        {lead.requiredServices && lead.requiredServices.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Required Services:</h4>
            <div className="flex flex-wrap gap-1">
              {lead.requiredServices.map((service, index) => (
                <span
                  key={index}
                  className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                >
                  {service.trim()}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Feedback */}
        {lead.feedbackFromClient && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-1">Client Feedback:</h4>
            <p className="text-sm text-gray-600">{lead.feedbackFromClient}</p>
          </div>
        )}

        {/* Comments */}
        {lead.comments && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-1">Comments:</h4>
            <p className="text-sm text-gray-600">{lead.comments}</p>
          </div>
        )}

        {/* Reminder Alert */}
        {reminderStatus && (
          <div className={`mb-4 p-2 rounded-lg ${reminderStatus.type === 'overdue' ? 'bg-red-50 border border-red-200' : reminderStatus.type === 'today' ? 'bg-orange-50 border border-orange-200' : 'bg-yellow-50 border border-yellow-200'}`}>
            <div className="flex items-center">
              <SafeIcon icon={FiAlertTriangle} className={`h-4 w-4 mr-2 ${reminderStatus.type === 'overdue' ? 'text-red-600' : reminderStatus.type === 'today' ? 'text-orange-600' : 'text-yellow-600'}`} />
              <p className={`text-sm font-medium ${reminderStatus.type === 'overdue' ? 'text-red-800' : reminderStatus.type === 'today' ? 'text-orange-800' : 'text-yellow-800'}`}>
                {reminderStatus.message}
              </p>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        {canEdit && lead.status !== 'converted' && lead.status !== 'rejected' && lead.status !== 'lost' && (
          <div className="mb-4 p-3 bg-green-50 rounded-lg">
            <p className="text-sm font-medium text-green-900 mb-2">Quick Actions:</p>
            <div className="flex flex-wrap gap-2">
              {quickStatusButtons
                .filter(btn => btn.status !== lead.status)
                .map(btn => (
                  <Button
                    key={btn.status}
                    size="sm"
                    variant={btn.variant}
                    onClick={() => onEdit(lead.id, { ...lead, status: btn.status })}
                  >
                    {btn.label}
                  </Button>
                ))}
              {lead.status === 'qualified' && (
                <Button
                  size="sm"
                  variant="primary"
                  onClick={handleConvertToClient}
                >
                  Convert to Client
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="pt-4 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500">
          <span>Created {format(new Date(lead.createdAt), 'MMM dd, yyyy')}</span>
          {lead.updatedAt && lead.updatedAt !== lead.createdAt && (
            <span>Updated {format(new Date(lead.updatedAt), 'MMM dd, yyyy')}</span>
          )}
        </div>
      </Card>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Lead"
        size="xl"
      >
        <LeadForm
          lead={lead}
          onSubmit={handleEdit}
          onCancel={() => setIsEditModalOpen(false)}
        />
      </Modal>
    </>
  );
};

export default LeadCard;